/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA ;
 */

import { get, writable } from 'svelte/store';
import { getIntegrationUserConfiguration } from '../dataServices/userIntegrationService';

/**
 * Store default state.
 *
 * @returns {{
 *  loading: boolean,
 *  config: import('../dataServices/userIntegrationService.js').UserIntegrationConfig|null
 * }}
 */
function createDefaultState() {
    return {
        loading: false,
        config: null
    };
}

function createStore() {
    const store = writable(createDefaultState());

    return {
        ...store,
        /**
         * Resets the store state to its default values.
         *
         * @returns {void}
         */
        reset() {
            store.set(createDefaultState());
        },
        /**
         * Fetches the integration service configuration from the backend API and updates the store.
         *
         * @returns {Promise<void>}
         *
         * @throws {Error} Throws an error if request fails.
         */
        async loadConfiguration() {
            store.update(s => ({ ...s, loading: true }));
            try {
                let config = get(store).config;
                if (!config) {
                    config = await getIntegrationUserConfiguration();
                    store.update(s => ({ ...s, config }));
                }
            } finally {
                store.update(s => ({ ...s, loading: false }));
            }
        }
    };
}

export const userIntegrationConfiguration = createStore();
